import { useState } from "react";
import { useApp } from "@/app/AppContext";
import { canClassify } from "@/domain/permissions";
import type { Intervention } from "@/domain/types";
import { useI18n } from "@/i18n/useI18n";
import { InterventionHistory } from "./InterventionHistory";

interface UpdatedMetaProps {
  item: Intervention;
  /** The details page renders history below the footer to keep Edit in place. */
  history?: {
    open: boolean;
    onToggle(): void;
  };
}

/** Creation and last-modified metadata; history loads only when opened. */
export function UpdatedMeta({ item, history }: UpdatedMetaProps) {
  const { t, locale } = useI18n();
  const { user } = useApp();
  const [localOpen, setLocalOpen] = useState(false);
  const open = history?.open ?? localOpen;
  const created = item.createdAt ? new Date(item.createdAt) : null;
  const updated = item.updatedAt ? new Date(item.updatedAt) : null;
  if (!created && !updated) return null;
  const date = (value: Date | null) => value && !Number.isNaN(value.getTime())
    ? value.toLocaleString(locale) : null;
  const canRead = !!item.dbId && canClassify(user);
  return (
    <div className="intervention-metadata">
      {date(created) && (
        <p className="form-updated-meta">
          {t("createdAt")}: {date(created)} {t("updatedBy")} {item.createdBy || t("updatedByUnknown")}
        </p>
      )}
      {date(updated) && (
        <p className="form-updated-meta">
          {canRead ? (
            <button
              type="button"
              className="intervention-history-link"
              onClick={history?.onToggle ?? (() => setLocalOpen((current) => !current))}
              aria-expanded={open}
            >
              {t("lastUpdated")}: {date(updated)}
            </button>
          ) : (
            <>{t("lastUpdated")}: {date(updated)}</>
          )}
          {" "}{t("updatedBy")} {item.updatedBy || t("updatedByUnknown")}
        </p>
      )}
      {!history && canRead && open && item.dbId && (
        <InterventionHistory interventionId={item.dbId} />
      )}
    </div>
  );
}
